import { useState, useRef, useEffect, useMemo, lazy, Suspense } from "react";
import { Link, useLocation } from "react-router-dom";
import { usePublicFabbriche, usePublicMarchi, usePublicServizi } from "@/hooks/usePublicEntities";
import { Button } from "@/components/ui/button";
import { Share, Globe, ExternalLink } from "lucide-react";
import { SearchBar } from "@/components/SearchBar";
import { ShareDialog } from "@/components/ShareDialog";
import { useLanguage } from "@/contexts/LanguageContext";
import MegaMenu from "@/components/MegaMenu";
import { UserMenu } from "@/components/auth/UserMenu";
import { useIsMobile } from "@/hooks/use-mobile";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

// Lazy-load the mobile drawer navigation so Vaul isn't in the desktop initial bundle
const LazyMobileNavigation = lazy(() =>
  import("@/components/MobileNavigation").then((m) => ({
    default: m.MobileNavigation,
  }))
);

const isExternal = (href: string) => href.startsWith("http");

const Header = () => {
  const [openMenu, setOpenMenu] = useState<string | null>(null);
  const [megaMenuOpen, setMegaMenuOpen] = useState<'FABBRICHE' | 'PROGETTI' | 'MARCHI' | null>(null);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [suppressedKey, setSuppressedKey] = useState<string | null>(null);
  const { language, setLanguage, t } = useLanguage();
  const location = useLocation();

  const isMobile = useIsMobile();
  const [isBelowLg, setIsBelowLg] = useState(() => typeof window !== 'undefined' && window.innerWidth < 1024);
  useEffect(() => {
    const mql = window.matchMedia('(max-width: 1023px)');
    const onChange = () => setIsBelowLg(mql.matches);
    onChange();
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, []);
  const showMobileNav = isMobile || isBelowLg;

  // Dynamic data for menu (kept in sync with admin dashboard)
  const { data: fabbricheData } = usePublicFabbriche();
  const { data: marchiData } = usePublicMarchi();
  const { data: serviziData } = usePublicServizi();

  // Keep submenu open while moving from trigger to panel
  const hoverTimeout = useRef<number | null>(null);
  const megaMenuTimeout = useRef<number | null>(null);

  // Reset suppression when route changes to a different page than the suppressed key's target
  useEffect(() => {
    setSuppressedKey(null);
    setMegaMenuOpen(null);
    setOpenMenu(null);
  }, [location.pathname]);

  const openHover = (name: string) => {
    if (suppressedKey === name) return;
    if (hoverTimeout.current) window.clearTimeout(hoverTimeout.current);
    setOpenMenu(name);
  };

  const closeHover = () => {
    if (hoverTimeout.current) window.clearTimeout(hoverTimeout.current);
    hoverTimeout.current = window.setTimeout(() => setOpenMenu(null), 150);
  };

  const openMegaMenu = (menu: 'FABBRICHE' | 'PROGETTI' | 'MARCHI') => {
    if (suppressedKey === menu) return;
    if (megaMenuTimeout.current) window.clearTimeout(megaMenuTimeout.current);
    setMegaMenuOpen(menu);
  };

  const closeMegaMenu = () => {
    if (megaMenuTimeout.current) window.clearTimeout(megaMenuTimeout.current);
    megaMenuTimeout.current = window.setTimeout(() => setMegaMenuOpen(null), 450);
  };

  // Reset suppression when pointer leaves the entire header area
  const handleHeaderMouseLeave = () => {
    setSuppressedKey(null);
  };

  // Mark a nav item as suppressed when clicking it while already on its page
  const handleNavClick = (item: { href: string; key: string }) => {
    const href = item.href;
    const isInternal = !href.startsWith('http');
    if (!isInternal) return;
    const path = location.pathname;
    const matches = path === href || (href !== '/' && path.startsWith(href + '/'));
    if (matches) {
      setSuppressedKey(item.key);
      // Close immediately, no timeout
      if (megaMenuTimeout.current) window.clearTimeout(megaMenuTimeout.current);
      if (hoverTimeout.current) window.clearTimeout(hoverTimeout.current);
      setMegaMenuOpen(null);
      setOpenMenu(null);
    }
  };

  const navItems = [{
    name: t('nav.gruppo'),
    href: "/gruppo",
    key: "GRUPPO"
  }, {
    name: t('nav.marchi'),
    href: "/marchi",
    key: "MARCHI"
  }, {
    name: t('nav.fabbriche'),
    href: "/fabbriche",
    key: "FABBRICHE"
  }, {
    name: t('nav.progetti'),
    href: "/progetti",
    key: "PROGETTI"
  }, {
    name: t('nav.shop'),
    href: "/shop",
    key: "SHOP"
  }, {
   name: "NOTIZIE",
   href: "/notizie",
   key: "NOTIZIE"
  }, {
   name: "CONTATTI",
   href: "/contatti",
   key: "CONTATTI"
  }];

  // Sub-menu dynamic for FABBRICHE / MARCHI / PROGETTI (driven by admin CMS)
  const dynamicMarchi = useMemo(
    () =>
      (marchiData || [])
        .filter((m) => m.show_in_menu && m.tipo !== 'difesa')
        .map((m) => ({
          label: m.nome,
          href: !m.has_internal_page && m.website_url ? m.website_url : `/brands/${m.slug}`,
        })),
    [marchiData]
  );
  const dynamicFabbriche = useMemo(
    () =>
      (fabbricheData || []).map((f) => ({
        label: f.nome_breve || f.nome,
        href: `/fabbriche/${f.slug}`,
      })),
    [fabbricheData]
  );
  const dynamicServizi = useMemo(
    () =>
      (serviziData || []).map((s) => ({
        label: s.nome,
        href: `/progetti/${s.slug}`,
      })),
    [serviziData]
  );

  const subMenus: Record<string, { label: string; href: string }[]> = {
    GRUPPO: [
      { label: t('gruppo.chiSiamo'), href: "/gruppo#chi-siamo" },
      { label: t('gruppo.valori'), href: "/gruppo#valori" },
      { label: t('gruppo.comeOperano'), href: "/gruppo#come-operano" },
      { label: "Certificazioni", href: "/certificazioni" },
    ],
    MARCHI: dynamicMarchi.length
      ? [...dynamicMarchi, { label: t('marchi.difesa'), href: "/marchi#marchi-difesa" }]
      : [],
    FABBRICHE: dynamicFabbriche,
    PROGETTI: dynamicServizi,
    NOTIZIE: [
      { label: "Blog", href: "/notizie/blog" },
      { label: "Eventi", href: "/notizie#eventi" },
      { label: "Cartella Stampa", href: "/notizie#cartella-stampa" },
    ],
  };

  const NavLinkOrA = ({ href, className, children, ...rest }: { href: string; className: string; children: React.ReactNode; [key: string]: any }) => {
    if (isExternal(href)) {
      return <a href={href} target="_blank" rel="noopener noreferrer" className={className} {...rest}>{children}</a>;
    }
    return <Link to={href} className={className} {...rest}>{children}</Link>;
  };

  return <>
      {/* Skip Link for Accessibility */}
      <a href="#main-content" className="skip-link">
        Vai al contenuto principale
      </a>
      
      {/* Main Header */}
      <header
        className="sticky top-0 z-[120] bg-background/95 backdrop-blur-md border-b border-border/30 shadow-elegant transition-all duration-300"
        role="banner"
        onMouseLeave={handleHeaderMouseLeave}
      >
        <div className="container mx-auto px-6">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link
              to="/"
              aria-label="Fabbriche Toscane - Torna alla homepage"
              className="flex items-center group focus-ring py-1 mr-2 transition-all duration-300"
            >
              <img 
                src="/lovable-uploads/fabbriche-toscane-logo-new.png" 
                alt="Fabbriche Toscane" 
                className="h-12 w-auto group-hover:opacity-80 transition-opacity duration-300"
                width="200"
                height="48"
                fetchPriority="high"
              />
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center h-16 space-x-6" role="navigation" aria-label="Navigazione principale">
              {navItems.map((item) => {
                const hasMegaMenu = ['FABBRICHE', 'PROGETTI', 'MARCHI'].includes(item.key);
                
                return (
                  <div
                    key={item.key}
                    className="relative group h-full flex items-center"
                    onMouseEnter={() => {
                      if (hasMegaMenu) {
                        openMegaMenu(item.key as 'FABBRICHE' | 'PROGETTI' | 'MARCHI');
                      } else {
                        openHover(item.key);
                      }
                    }}
                    onMouseLeave={() => {
                      if (hasMegaMenu) {
                        closeMegaMenu();
                      } else {
                        closeHover();
                      }
                    }}
                  >
                    <NavLinkOrA
                      href={item.href}
                      onClick={() => handleNavClick(item)}
                      className="flex items-center h-full text-xl font-brandbe font-bold tracking-[0.1em] text-foreground hover:text-primary transition-all duration-300 uppercase story-link hover-scale focus-ring px-2"
                      aria-expanded={subMenus[item.key] && subMenus[item.key].length > 0 ? (openMenu === item.key || megaMenuOpen === item.key) : undefined}
                      aria-haspopup={subMenus[item.key] && subMenus[item.key].length > 0 ? "true" : undefined}
                    >
                      {item.name}
                      {isExternal(item.href) && <ExternalLink className="inline-block w-3 h-3 ml-1 opacity-50" />}
                    </NavLinkOrA>
                    {/* Simple Dropdown for non-mega menu items */}
                    {!hasMegaMenu && subMenus[item.key] && subMenus[item.key].length > 0 && openMenu === item.key && (
                      <div
                        className="absolute left-0 top-full mt-2 z-[100] bg-background backdrop-blur-md border border-border p-1 min-w-[240px] dropdown-enter"
                        style={{ boxShadow: 'var(--shadow-dropdown)' }}
                        onMouseEnter={() => openHover(item.key)}
                        onMouseLeave={closeHover}
                        role="menu"
                        aria-label={`Menu ${item.name}`}
                      >
                        {subMenus[item.key].map((sub) => (
                          <NavLinkOrA
                            key={sub.label}
                            href={sub.href}
                            className="block px-3 py-2 text-sm font-body text-foreground hover:bg-muted hover:text-primary focus:bg-muted focus:text-primary focus-ring transition-all duration-200"
                            role="menuitem"
                          >
                            {sub.label}
                          </NavLinkOrA>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </nav>

            {/* Right Icons - Desktop and Mobile optimized */}
            <div className="flex items-center gap-1 sm:gap-2">
              {/* Mobile: Only essential elements visible */}
              <div className="flex items-center gap-1 lg:hidden">
                {/* User Menu */}
                <UserMenu />
                
                {/* Mobile Navigation Drawer */}
                {showMobileNav && (
                  <Suspense fallback={null}>
                    <LazyMobileNavigation navItems={navItems} subMenus={subMenus} />
                  </Suspense>
                )}
                
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button 
                      variant="ghost"
                      size="icon" 
                      className="h-10 w-10 focus-ring hover:bg-muted transition-all duration-200"
                      aria-label="Seleziona lingua"
                    >
                      <Globe className="w-4 h-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="z-[100] bg-background">
                    <DropdownMenuItem 
                      onClick={() => setLanguage('it')}
                      className={language === 'it' ? 'bg-muted' : ''}
                    >
                      🇮🇹 Italiano
                    </DropdownMenuItem>
                    <DropdownMenuItem 
                      onClick={() => setLanguage('en')}
                      className={language === 'en' ? 'bg-muted' : ''}
                    >
                      🇬🇧 English
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>

              {/* Desktop: Full feature set */}
              <div className="hidden lg:flex items-center gap-3">
                <SearchBar />
                
                <Button 
                  variant="ghost"
                  size="icon" 
                  className="h-10 w-10 focus-ring hover:bg-muted transition-all duration-200 btn-modern"
                  aria-label="Condividi questa pagina"
                  onClick={() => setShareDialogOpen(true)}
                >
                  <Share className="w-4 h-4" />
                </Button>

                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button 
                      variant="ghost"
                      size="icon" 
                      className="h-10 w-10 focus-ring hover:bg-muted transition-all duration-200 btn-modern"
                      aria-label="Seleziona lingua"
                    >
                      <Globe className="w-4 h-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="z-[100] bg-background">
                    <DropdownMenuItem 
                      onClick={() => setLanguage('it')}
                      className={language === 'it' ? 'bg-muted' : ''}
                    >
                      🇮🇹 Italiano
                    </DropdownMenuItem>
                    <DropdownMenuItem 
                      onClick={() => setLanguage('en')}
                      className={language === 'en' ? 'bg-muted' : ''}
                    >
                      🇬🇧 English
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>

                {/* User Menu */}
                <UserMenu />
              </div>
            </div>
          </div>

        </div>
      {/* Mega Menu */}
      <MegaMenu 
        isOpen={megaMenuOpen !== null} 
        menuType={megaMenuOpen}
        onClose={closeMegaMenu}
        onMouseEnter={() => {
          if (megaMenuTimeout.current) window.clearTimeout(megaMenuTimeout.current);
        }}
      />
      </header>


      <ShareDialog open={shareDialogOpen} onOpenChange={setShareDialogOpen} />
    </>;
  };
  export default Header;
